/**
 * Mobile Kanban Component
 *
 * Touch-optimized Kanban board for technician task management.
 * Features horizontal scroll snap, swipe gestures and pull-to-refresh.
 *
 * The pull-to-refresh gesture lives on the horizontal scroll container so it
 * also catches touches that start inside a column's task list. It only engages
 * when the gesture is more vertical than horizontal (horizontal belongs to the
 * card swipe / column paging) and the column list under the finger is already
 * scrolled to the top.
 *
 * @module pages/technician/tasks/MobileKanban
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import MobileTaskCard from './MobileTaskCard';
import { TASK_STATUS, TASK_PRIORITY } from '../../../utils/constants';
import './MobileKanban.css';

const COLUMNS = [
  {
    id: TASK_STATUS.PENDING,
    title: 'Pendientes',
    icon: 'bi-hourglass-split',
    emptyIcon: 'bi-inbox',
    emptyText: 'No hay tareas pendientes',
  },
  {
    id: TASK_STATUS.IN_PROGRESS,
    title: 'En Progreso',
    icon: 'bi-gear-fill',
    emptyIcon: 'bi-play-circle',
    emptyText: 'No hay tareas en progreso',
  },
  {
    id: TASK_STATUS.COMPLETED,
    title: 'Completadas',
    icon: 'bi-check-circle-fill',
    emptyIcon: 'bi-trophy',
    emptyText: 'No hay tareas completadas',
  },
];

// Pull-to-refresh tuning (mirrors the swipe thresholds in MobileTaskCard)
const PULL_THRESHOLD = 60;   // pulled px needed to fire onRefresh
const MAX_PULL = 100;        // how far the indicator can travel
const PULL_RESISTANCE = 0.5; // finger travel -> indicator travel
const DIRECTION_DEADZONE = 10; // px before we decide vertical vs horizontal

const MobileKanban = ({
  tasks = [],
  loading = false,
  onRefresh,
  onStatusChange,
  onCreateTask,
  filters,
  onFilterChange,
  projects = [],
}) => {
  const scrollContainerRef = useRef(null);
  const [activeColumnIndex, setActiveColumnIndex] = useState(0);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [filterType, setFilterType] = useState(null); // 'project' | 'priority' | 'myTasks'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const isVerticalPull = useRef(false);
  const pullScroller = useRef(null); // the column task list under the finger

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped = {
      [TASK_STATUS.PENDING]: [],
      [TASK_STATUS.IN_PROGRESS]: [],
      [TASK_STATUS.COMPLETED]: [],
    };

    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });

    return grouped;
  }, [tasks]);

  // Show swipe hint on first load
  useEffect(() => {
    const hasSeenHint = localStorage.getItem('kanban-swipe-hint-seen');
    if (!hasSeenHint && tasks.length > 0) {
      setShowSwipeHint(true);
      setTimeout(() => {
        setShowSwipeHint(false);
        localStorage.setItem('kanban-swipe-hint-seen', 'true');
      }, 3000);
    }
  }, [tasks.length]);

  // Handle scroll to detect active column
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const columnWidth = container.offsetWidth;
    const newIndex = Math.round(scrollLeft / columnWidth);

    if (newIndex !== activeColumnIndex && newIndex >= 0 && newIndex < COLUMNS.length) {
      setActiveColumnIndex(newIndex);
    }
  }, [activeColumnIndex]);

  // Pull-to-refresh: start tracking the gesture
  const handlePullStart = useCallback((e) => {
    if (!onRefresh || isRefreshing) return;

    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    touchStartX.current = touch.clientX;
    isVerticalPull.current = false;
    // Vertical scrolling happens inside each column's task list, not on the
    // horizontal container, so gate the pull on THAT element's scrollTop.
    pullScroller.current =
      typeof e.target?.closest === 'function' ? e.target.closest('.kanban-task-list') : null;
  }, [onRefresh, isRefreshing]);

  const handlePullMove = useCallback((e) => {
    if (!onRefresh || isRefreshing) return;

    const touch = e.touches[0];
    const diffY = touch.clientY - touchStartY.current;
    const diffX = touch.clientX - touchStartX.current;

    if (!isVerticalPull.current) {
      if (Math.abs(diffY) < DIRECTION_DEADZONE) return;
      // Horizontal wins -> card swipe or column paging, not our gesture
      if (Math.abs(diffY) <= Math.abs(diffX)) return;
      if (diffY <= 0) return;
      // Only pull when the list is already at the top
      if (pullScroller.current && pullScroller.current.scrollTop > 0) return;
      isVerticalPull.current = true;
    }

    if (diffY <= 0) {
      setPullDistance(0);
      return;
    }

    setPullDistance(Math.min(diffY * PULL_RESISTANCE, MAX_PULL));
  }, [onRefresh, isRefreshing]);

  const handlePullEnd = useCallback(async () => {
    const shouldRefresh = isVerticalPull.current && pullDistance >= PULL_THRESHOLD;

    isVerticalPull.current = false;
    pullScroller.current = null;
    setPullDistance(0);

    if (!shouldRefresh || !onRefresh || isRefreshing) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch (err) {
      // The parent owns error reporting; here we only make sure a failed
      // refresh never leaves the spinner stuck on screen.
      console.error('Error al actualizar tareas:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing, pullDistance]);

  // Scroll to specific column
  const scrollToColumn = (index) => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const columnWidth = container.offsetWidth;
    container.scrollTo({
      left: columnWidth * index,
      behavior: 'smooth',
    });
  };

  // Handle status change from swipe
  const handleStatusChange = async (taskId, newStatus) => {
    if (onStatusChange) {
      await onStatusChange(taskId, newStatus);
    }
  };

  // Filter handlers
  const openFilter = (type) => {
    setFilterType(type);
    setShowFilterDropdown(true);
  };

  const closeFilter = () => {
    setShowFilterDropdown(false);
    setFilterType(null);
  };

  const applyFilter = (type, value) => {
    if (onFilterChange) {
      onFilterChange({ ...filters, [type]: value });
    }
    closeFilter();
  };

  const clearFilters = () => {
    if (onFilterChange) {
      onFilterChange({});
    }
    closeFilter();
  };

  const hasActiveFilters = filters && (filters.project || filters.priority || filters.myTasks);

  if (loading && tasks.length === 0) {
    return (
      <div className="mobile-kanban mobile-kanban--loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="mobile-kanban">
      {/* Pull to Refresh indicator */}
      {(isRefreshing || pullDistance > 0) && (
        <div
          className={`pull-to-refresh ${isRefreshing ? 'pull-to-refresh--active' : ''}`}
          style={isRefreshing ? undefined : { transform: `translateY(${pullDistance}px)` }}
          data-testid="pull-to-refresh"
          role="status"
          aria-live="polite"
        >
          <div
            className={`pull-to-refresh__spinner ${isRefreshing ? '' : 'pull-to-refresh__spinner--idle'}`}
            data-testid="pull-to-refresh-spinner"
            style={isRefreshing ? undefined : { transform: `rotate(${pullDistance * 3}deg)` }}
          ></div>
          <span className="visually-hidden">
            {isRefreshing ? 'Actualizando tareas' : 'Desliza para actualizar'}
          </span>
        </div>
      )}

      {/* Filter Bar */}
      <div className="kanban-filter-bar">
        <button
          className={`kanban-filter-btn ${filters?.project ? 'kanban-filter-btn--active' : ''}`}
          onClick={() => openFilter('project')}
        >
          <i className="bi bi-folder"></i>
          {filters?.project
            ? projects.find(p => p.id === filters.project)?.title || 'Proyecto'
            : 'Proyecto'}
        </button>

        <button
          className={`kanban-filter-btn ${filters?.priority ? 'kanban-filter-btn--active' : ''}`}
          onClick={() => openFilter('priority')}
        >
          <i className="bi bi-flag"></i>
          {filters?.priority
            ? { urgent: 'Urgente', high: 'Alta', medium: 'Media', low: 'Baja' }[filters.priority]
            : 'Prioridad'}
        </button>

        <button
          className={`kanban-filter-btn ${filters?.myTasks ? 'kanban-filter-btn--active' : ''}`}
          onClick={() => applyFilter('myTasks', !filters?.myTasks)}
        >
          <i className="bi bi-person-check"></i>
          Mis Tareas
        </button>

        {hasActiveFilters && (
          <button
            type="button"
            className="kanban-filter-clear"
            onClick={clearFilters}
            aria-label="Quitar todos los filtros"
          >
            <i className="bi bi-x-circle"></i>
          </button>
        )}
      </div>

      {/* Column Indicators (Dots) */}
      <div className="kanban-indicators">
        {COLUMNS.map((column, index) => (
          <button
            key={column.id}
            className={`kanban-indicator kanban-indicator--${column.id} ${index === activeColumnIndex ? 'kanban-indicator--active' : ''}`}
            onClick={() => scrollToColumn(index)}
            aria-label={`Ver ${column.title}`}
          />
        ))}
      </div>

      {/* Column Labels */}
      <div className="kanban-column-labels">
        {COLUMNS.map((column, index) => (
          <button
            key={column.id}
            className={`kanban-column-label kanban-column-label--${column.id} ${index === activeColumnIndex ? 'kanban-column-label--active' : ''}`}
            onClick={() => scrollToColumn(index)}
          >
            <span className="kanban-column-label__count">
              {tasksByStatus[column.id]?.length || 0}
            </span>
            <span className="kanban-column-label__text">{column.title}</span>
          </button>
        ))}
      </div>

      {/* Horizontal Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="kanban-scroll-container"
        onScroll={handleScroll}
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
        onTouchCancel={handlePullEnd}
      >
        {COLUMNS.map((column) => (
          <div key={column.id} className="kanban-column">
            <div className="kanban-column__header">
              <div className="kanban-column__title">
                <i className={`bi ${column.icon}`}></i>
                {column.title}
              </div>
              <span className={`kanban-column__badge kanban-column__badge--${column.id}`}>
                {tasksByStatus[column.id]?.length || 0}
              </span>
            </div>

            <div className="kanban-task-list" data-testid={`kanban-list-${column.id}`}>
              {tasksByStatus[column.id]?.length === 0 ? (
                <div className="kanban-task-list--empty">
                  <i className={`bi ${column.emptyIcon}`}></i>
                  <p>{column.emptyText}</p>
                </div>
              ) : (
                tasksByStatus[column.id].map(task => (
                  <MobileTaskCard
                    key={task.id}
                    task={task}
                    onStatusChange={handleStatusChange}
                    showSwipeActions={column.id !== TASK_STATUS.COMPLETED}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Swipe Hints */}
      {showSwipeHint && activeColumnIndex < COLUMNS.length - 1 && (
        <div className="swipe-hint swipe-hint--right">
          <span>Desliza para ver mas</span>
          <i className="bi bi-chevron-right"></i>
        </div>
      )}

      {/* FAB - Create Task */}
      {onCreateTask && (
        <button className="kanban-fab" onClick={onCreateTask} aria-label="Crear tarea">
          <i className="bi bi-plus-lg"></i>
        </button>
      )}

      {/* Filter Dropdown */}
      {showFilterDropdown && (
        <>
          <div className="filter-dropdown-overlay" onClick={closeFilter}></div>
          <div className="filter-dropdown">
            <div className="filter-dropdown__header">
              <h3 className="filter-dropdown__title">
                {filterType === 'project' && 'Filtrar por Proyecto'}
                {filterType === 'priority' && 'Filtrar por Prioridad'}
              </h3>
              <button
                type="button"
                className="filter-dropdown__close"
                onClick={closeFilter}
                aria-label="Cerrar filtros"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="filter-dropdown__content">
              {filterType === 'project' && (
                <div className="filter-section">
                  <button
                    className={`filter-option ${!filters?.project ? 'filter-option--active' : ''}`}
                    onClick={() => applyFilter('project', null)}
                  >
                    <span className="filter-option__radio"></span>
                    <span className="filter-option__text">Todos los proyectos</span>
                  </button>
                  {projects.map(project => (
                    <button
                      key={project.id}
                      className={`filter-option ${filters?.project === project.id ? 'filter-option--active' : ''}`}
                      onClick={() => applyFilter('project', project.id)}
                    >
                      <span className="filter-option__radio"></span>
                      <span className="filter-option__text">
                        {project.title || project.project_number}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {filterType === 'priority' && (
                <div className="filter-section">
                  <button
                    className={`filter-option ${!filters?.priority ? 'filter-option--active' : ''}`}
                    onClick={() => applyFilter('priority', null)}
                  >
                    <span className="filter-option__radio"></span>
                    <span className="filter-option__text">Todas las prioridades</span>
                  </button>
                  {Object.entries({
                    [TASK_PRIORITY.URGENT]: 'Urgente',
                    [TASK_PRIORITY.HIGH]: 'Alta',
                    [TASK_PRIORITY.MEDIUM]: 'Media',
                    [TASK_PRIORITY.LOW]: 'Baja',
                  }).map(([value, label]) => (
                    <button
                      key={value}
                      className={`filter-option ${filters?.priority === value ? 'filter-option--active' : ''}`}
                      onClick={() => applyFilter('priority', value)}
                    >
                      <span className="filter-option__radio"></span>
                      <span className="filter-option__text">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-dropdown__actions">
              <button className="filter-dropdown__clear" onClick={clearFilters}>
                Limpiar filtros
              </button>
              <button className="filter-dropdown__apply" onClick={closeFilter}>
                Aplicar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

MobileKanban.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      priority: PropTypes.string,
      due_date: PropTypes.string,
      project: PropTypes.object,
    })
  ),
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onStatusChange: PropTypes.func,
  onCreateTask: PropTypes.func,
  filters: PropTypes.shape({
    project: PropTypes.string,
    priority: PropTypes.string,
    myTasks: PropTypes.bool,
  }),
  onFilterChange: PropTypes.func,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      project_number: PropTypes.string,
    })
  ),
};

export default MobileKanban;
